import {h, Fragment} from "preact"
import { useAppStateSelector } from "@nosto/preact"
import { useEffect, useRef, useState, useCallback } from "preact/hooks";
import { themes } from "../config"

export default function useProductGroup() {
    const { products, query } = useAppStateSelector(state => ({ 
        products: state.response.products, 
        query: state.query
    }));
    const [ groupedProducts, setGroupedProducts ] = useState([]);
    const lastQueryRef = useRef(undefined);

    const fetchGroups = useCallback(async (values) => {
        try {
            const res = await fetch('https://api.nosto.com/v1/graphql', {
                method: 'POST',
                headers: new Headers({
                    'Content-Type': 'application/json',
                    'Authorization': 'Basic ' + btoa(":" + themes.nosto)
                }),
                mode: 'cors',
                body: JSON.stringify({
                    query: `
                        query {
                            products(limit: 100, filter: { tags1: ${JSON.stringify(values)} } ) {
                                products {
                                    productId,
                                    url,
                                    name, 
                                    tags1, 
                                    imageUrl, 
                                    alternateImageUrls,
                                    brand, 
                                    ratingValue,
                                    availability,
                                    price, 
                                    listPrice,
                                    attributes {
                                        key,
                                        value
                                    },
                                    skus {
                                        id
                                        availability,
                                        attributes {
                                            key,
                                            value
                                        }
                                    }
                                }
                            }
                        }
                    `
                })
            });
            const data = await res.json();
            return data;
        } catch (err) {
            console.error(err);
            return null;
        }
    }, []);

    useEffect(() => {   
        if(!products?.hits || products.hits?.length < 1) return;

        const isNewQuery = query?.query && query.query !== lastQueryRef?.current;
        if(isNewQuery) { 
            lastQueryRef.current = query.query;
        }

        const groupsToFetch = [];
        const dataToKeep = [];
        products.hits.forEach(product => {
            const groupField = product.tags1?.find(f => f.startsWith("colourway-"));
            const groupInData = groupedProducts.find(group => group.groupId === groupField);
            if(groupField && !groupInData && !groupsToFetch.includes(groupField)) {
                groupsToFetch.push(groupField);
            } else if (isNewQuery && groupInData) {
                dataToKeep.push(groupInData);
            }
        });

        if(groupsToFetch?.length < 1) return
        fetchGroups(groupsToFetch)
        .then(data => {
            if(data.errors){
                throw new Error(data.errors.map(error => error.message).join(', '))
            }
            const groupProducts = data?.data?.products?.products?.filter(product => product.availability !== 'Invisible');
            if(!groupProducts?.length || groupProducts.length < 1) return;
            const updatedData = (isNewQuery) ? dataToKeep : [...groupedProducts];
            
            groupsToFetch.forEach(groupField => {
                const matchingProducts = groupProducts
                    .filter(product => !!product.tags1.includes(groupField))
                    .map(p => {
                        const color = p.attributes.find(attr => attr.key === "productinfo-colour")?.value || p.tags1?.find(tag => tag.startsWith('color-'))?.replace('color-', '');
                        return { ...p, ...(color && { color }) }
                    });
                const groupData = { groupId: groupField, products: matchingProducts };
                updatedData.push(groupData);
            });
            setGroupedProducts(updatedData);
        })
        .catch(error => console.warn(error))

    }, [ fetchGroups, products?.hits, query?.query ]);

    return {
        groupedProducts
    }
}